import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
// import { useResponseHandler } from '../../../../hooks';
// import { InputField, TextAreaField } from '../../../../components/formFields';
import formInitialValues from '../formik/create/formModels/formInitialValues';
import validationSchema from '../formik/create/formModels/validationSchema';
import { rulesAndAlertsFormModel } from '../formik/create/formModels/rulesAndAlertsFormModel';
import {
  storeRulesAndAlert,
  getAvailableUsersForAlertsStore,
} from '../../../../store/ratings/thunks';
import { alpha } from '@mui/material/styles';
import InputField from '../../../../components/formFields/InputField';
import TextAreaField from '../../../../components/formFields/TextAreaField';
import { useResponseHandler } from '../../../../hooks/useResponseHandler';

const { formField } = rulesAndAlertsFormModel;

interface FormRulesProps {
  ruleData?: any;
  onCancel: () => void;
  onSuccess: () => void;
}

export const FormRules: React.FC<FormRulesProps> = ({ ruleData, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const { handleResponse, ResponseComponents } = useResponseHandler();

  const { available_users_for_alerts_select } = useSelector((state: any) => state.ratingsDevices);

  const [searchUser, setSearchUser] = useState('');
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<any[]>([]);

  const isEdit = !!ruleData;
  const ruleDetail = isEdit && ruleData.data ? ruleData.data : ruleData;

  useEffect(() => {
    if (!isEdit) {
      dispatch(getAvailableUsersForAlertsStore());
    }
  }, [isEdit]);

  useEffect(() => {
    let availableList: any[] = [];

    if (isEdit && ruleData?.users) {
      availableList = ruleData.users;
    } else if (available_users_for_alerts_select?.length > 0) {
      availableList = available_users_for_alerts_select;
    }

    if (availableList.length > 0) {
      const mappedAvailableList = availableList.map((u: any) => {
        let id = u.id || u._id || u.userId;
        let name = u.name || u.firstName;

        if (!id) {
          // Check which property is the UUID
          if (u.value && String(u.value).includes('-') && String(u.value).length > 20) {
            id = u.value;
            name = u.label || u.name;
          } else if (u.label && String(u.label).includes('-') && String(u.label).length > 20) {
            id = u.label;
            name = u.value || u.name;
          } else {
            id = u.label;
            name = u.value;
          }
        }
        return { ...u, label: id, value: name, email: u.email };
      });

      // Filter out already assigned users from available list
      setAvailableUsers(
        mappedAvailableList.filter(
          (u: any) =>
            !assignedUsers.some(
              (au: any) =>
                (u.label && au.label && u.label === au.label) ||
                (u.email && au.email && u.email === au.email) ||
                u === au
            )
        )
      );
    } else {
      setAvailableUsers([]);
    }
  }, [available_users_for_alerts_select, assignedUsers, isEdit, ruleData]);

  // Populate data for edit

  const initialVals =
    isEdit && ruleDetail
      ? {
          [formField.name.name]: ruleDetail.name || '',
          [formField.description.name]: ruleDetail.description || '',
          [formField.status.name]: ruleDetail.status === 1,
          [formField.assignedUsers.name]: ruleDetail.users || [],
        }
      : formInitialValues;

  useEffect(() => {
    if (isEdit && ruleDetail?.users) {
      // Asignar usuarios que vienen del backend
      const mappedUsers = ruleDetail.users.map((ru: any) => {
        const u = ru.user || ru;
        let id = u.id || u._id || u.userId;
        let name = u.name || u.firstName;

        if (!id) {
          if (u.value && String(u.value).includes('-') && String(u.value).length > 20) {
            id = u.value;
            name = u.label || u.name;
          } else if (u.label && String(u.label).includes('-') && String(u.label).length > 20) {
            id = u.label;
            name = u.value || u.name;
          } else {
            id = u.label;
            name = u.value;
          }
        }

        return {
          ...u,
          label: id,
          value: name,
          email: u.email,
        };
      });

      setAssignedUsers(mappedUsers);
    } else {
      setAssignedUsers([]);
    }
  }, [isEdit, ruleDetail]);

  const handleAssignUser = (user: any, setFieldValue: any) => {
    const newAssigned = [...assignedUsers, user];
    setAssignedUsers(newAssigned);
    setFieldValue(formField.assignedUsers.name, newAssigned);
    setSearchUser('');
  };

  const handleRemoveUser = (user: any, setFieldValue: any) => {
    const newAssigned = assignedUsers.filter(
      (u) =>
        !(
          (u.label && user.label && u.label === user.label) ||
          (u.email && user.email && u.email === user.email) ||
          u === user
        )
    );
    setAssignedUsers(newAssigned);
    setFieldValue(formField.assignedUsers.name, newAssigned);
  };

  const onSubmit = async (values: any, actions: any) => {
    const payload = {
      name: values[formField.name.name],
      description: values[formField.description.name],
      assignedUsers: assignedUsers.map((u) => u.label),
    };

    const response = await dispatch(storeRulesAndAlert(payload, isEdit, ruleDetail?.id));
    await handleResponse(response, isEdit ? 'actualizar regla' : 'crear regla', true);

    if (response?.ok) {
      actions.resetForm();
      setTimeout(() => {
        onSuccess();
      }, 1500); // Dar tiempo para mostrar el alert antes de desmontar
    }
  };

  const filteredAvailable = availableUsers.filter(
    (u) =>
      (u.value || '').toLowerCase().includes(searchUser.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(searchUser.toLowerCase())
  );

  return (
    <Card
      sx={{
        p: 0,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
        border: '1px solid #f0f0f0',
      }}
    >
      {ResponseComponents}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box sx={{ p: 1, bgcolor: alpha('#4A148C', 0.08), borderRadius: 1.5, display: 'flex' }}>
          <EditIcon sx={{ color: '#4A148C' }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#4A148C', fontSize: '1.1rem' }}>
          {isEdit ? 'Editar Regla' : 'Crear Regla'}
        </Typography>
      </Box>

      <Formik
        initialValues={initialVals}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ setFieldValue, values, isSubmitting, resetForm }) => (
          <Form>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={4}>
                {/* Left Column: Rule Info */}
                <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, color: '#2e263d', mb: 2, fontSize: '0.85rem' }}
                  >
                    Información de la Regla
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}
                    >
                      Nombre de la regla <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <InputField
                      autoComplete="off"
                      name={formField.name.name}
                      placeholder="Ej. Alerta de clientes inactivos"
                      size="small"
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}
                    >
                      Descripción
                    </Typography>
                    <TextAreaField
                      name={formField.description.name}
                      placeholder="Describe la condición que activará esta alerta"
                      rows={4}
                    />
                  </Box>

                  {/* <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                                            Estado
                                        </Typography>
                                        <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch 
                                                        checked={values[formField.status.name] as boolean} 
                                                        onChange={(e) => setFieldValue(formField.status.name, e.target.checked)} 
                                                        color="primary" 
                                                    />
                                                }
                                                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {values[formField.status.name] ? 'Activa' : 'Inactiva'}
                                                </Typography>}
                                            />
                                        </Box>
                                    </Box> */}
                </Grid>

                {/* Right Column: User Assignment */}
                <Grid size={{ xs: 12, sm: 12, md: 8 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, color: '#4A148C', mb: 2, fontSize: '0.85rem' }}
                  >
                    Asignación de Usuarios
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Available Users */}
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}
                      >
                        Buscar usuarios
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Buscar por nombre o email..."
                          value={searchUser}
                          onChange={(e) => setSearchUser(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
                      >
                        Usuarios disponibles
                      </Typography>
                      <List
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          p: 0,
                          maxHeight: 250,
                          overflowY: 'auto',
                        }}
                      >
                        {filteredAvailable.length > 0 ? (
                          filteredAvailable.map((user: any, index: number) => (
                            <ListItem
                              key={user.label || user.email || index}
                              divider
                              sx={{ p: 1.5, '&:hover': { bgcolor: alpha('#4A148C', 0.02) } }}
                              secondaryAction={
                                <IconButton
                                  size="small"
                                  onClick={() => handleAssignUser(user, setFieldValue)}
                                  sx={{
                                    color: 'primary.main',
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    p: 0.5,
                                    borderRadius: 1,
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              }
                            >
                              <ListItemAvatar sx={{ minWidth: 40 }}>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: alpha('#4A148C', 0.1),
                                    color: '#4A148C',
                                    fontSize: '0.8rem',
                                    fontWeight: 800,
                                  }}
                                >
                                  {user.value?.substring(0, 2).toUpperCase()}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: '#2e263d', fontSize: '0.75rem' }}
                                  >
                                    {user.value}
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                                  >
                                    {user.email}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))
                        ) : (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              No hay usuarios disponibles
                            </Typography>
                          </Box>
                        )}
                      </List>
                    </Grid>

                    {/* Transfer Indicators */}
                    {/* item xs={12} sm={2} */}
                    <Grid
                      size={{ xs: 12, md: 2 }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <ArrowForwardIcon
                        color="action"
                        sx={{ display: { xs: 'none', sm: 'block' } }}
                      />
                      <ArrowBackIcon color="action" sx={{ display: { xs: 'none', sm: 'block' } }} />
                    </Grid>

                    {/* Assigned Users */}
                    {/* item xs={12} sm={5} */}
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          mb: 1,
                          display: 'block',
                          mt: { xs: 2, sm: 6.5 },
                        }}
                      >
                        Usuarios asignados ({assignedUsers.length})
                      </Typography>
                      <List
                        sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          p: 0,
                          minHeight: 250,
                          maxHeight: 250,
                          overflowY: 'auto',
                          bgcolor: alpha('#4A148C', 0.01),
                        }}
                      >
                        {assignedUsers.length > 0 ? (
                          assignedUsers.map((user: any, index: number) => (
                            <ListItem
                              key={user.label || user.email || index}
                              divider
                              sx={{
                                p: 1.5,
                                bgcolor: '#ffffff',
                                m: 1,
                                borderRadius: 2,
                                width: 'auto',
                                border: '1px solid #f0f0f0',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                              }}
                              secondaryAction={
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveUser(user, setFieldValue)}
                                  sx={{ color: 'error.main' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              }
                            >
                              <ListItemAvatar sx={{ minWidth: 40 }}>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: alpha('#4A148C', 0.1),
                                    color: '#4A148C',
                                    fontSize: '0.8rem',
                                    fontWeight: 800,
                                  }}
                                >
                                  {user.value?.substring(0, 2).toUpperCase()}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: '#2e263d', fontSize: '0.75rem' }}
                                  >
                                    {user.value}
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                                  >
                                    {user.email}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))
                        ) : (
                          <Box
                            sx={{
                              p: 5,
                              textAlign: 'center',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Ningún usuario asignado
                            </Typography>
                          </Box>
                        )}
                      </List>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                p: 2.5,
                borderTop: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                bgcolor: alpha('#000', 0.01),
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  resetForm();
                  setAssignedUsers([]);
                  onCancel();
                }}
                sx={{ borderRadius: 1.5, fontWeight: 700, textTransform: 'none' }}
              >
                Cancelar
              </Button>
              {!isEdit && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    resetForm();
                    setAssignedUsers([]);
                  }}
                  sx={{ borderRadius: 1.5, fontWeight: 700, textTransform: 'none' }}
                >
                  Limpiar
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                startIcon={<SaveIcon sx={{ fontSize: '1rem' }} />}
                sx={{
                  bgcolor: '#4A148C',
                  '&:hover': { bgcolor: '#380b6b' },
                  borderRadius: 1.5,
                  px: 3,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Guardar Regla
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Card>
  );
};
